/**
 * Le vocabulaire PARTAGÉ des clients et des bons — extrait pour éviter le cycle d'import entre les
 * écrans qui s'en servent (liste clients, fiche client, registre « Titres & bons »).
 * Rien d'autre n'entre ici : ce module reste le plus petit dénominateur de ses consommateurs.
 */
import { NEGATIVE_BALANCE_CAUSE_TEXT, type NegativeBalanceCause } from '@orgazone/core-loyalty';
import { VOUCHER_STATE_LABELS } from '@orgazone/core-vouchers';
import type { BadgeTone, IconName } from '@orgazone/ui-tokens';
import { key, t, type Translatable } from '../i18n';

/** Les paliers connus — couleurs relevées (`tier`) ; une valeur inconnue s'affiche telle quelle en neutre. */
/**
 * LA PASTILLE D'UN PALIER — ses couleurs dérivées de la couleur du BARÈME du magasin (`fid.tiers`).
 *
 * ⚠️ LE LIBELLÉ ET LA COULEUR VIENNENT DU SERVEUR (`tierDisplay`, la lecture partagée avec la caisse),
 *    jamais d'une table d'écran : celle-ci était indexée `S`/`G`/`P` quand le serveur écrit `silver`,
 *    `gold`, `plat` — aucun palier réel n'y correspondait, et la fiche affichait « gold » en gris.
 * ⚠️ LE LIBELLÉ EST UNE DONNÉE du magasin, il ne se traduit pas.
 * ⚠️ SANS COULEUR (palier absent du barème), un ton neutre — jamais une couleur inventée.
 */
export function palierPastille(d: { label: string; color: string | null }): { l: string; bg: string; fg: string } {
  if (!d.color) return { l: d.label, bg: 'var(--ink-100)', fg: 'var(--ink-600)' };
  return {
    l: d.label,
    bg: `color-mix(in oklab, ${d.color} 18%, var(--paper))`,
    fg: `color-mix(in oklab, ${d.color} 75%, var(--ink-900))`,
  };
}

/** ISO AAAA-MM-JJ → JJ/MM/AAAA (— si absente). */
export const bdL = (b: string | null): string => (b ? `${b.slice(8, 10)}/${b.slice(5, 7)}/${b.slice(0, 4)}` : '—');

/**
 * LES ÉTATS D'UN TITRE, tels que le CŒUR les nomme (`core-vouchers.voucherState`) — libellé et TON
 * de pastille. Partagé par la fiche client et le registre « Titres & bons » : deux tables auraient
 * fini par nommer le même état autrement d'un écran à l'autre.
 * ⚠️ `partial` EXISTE, et c'est le plus important : un chèque cadeau entamé n'est ni « en cours »
 * ni « utilisé » — le confondre avec l'un des deux fausse l'engagement du magasin.
 * ⚠️ UN TON (`BadgeTone`), JAMAIS UNE COULEUR : les rôles `ok-*`/`amber-*` portent une surcharge de
 * thème sombre que la rampe de marque `teal-*` n'a pas — une pastille peinte en `teal-100` reste
 * claire sur écran sombre (précédent relevé dans `ui-tokens/badge.ts`).
 */
export const ETAT_TITRE: Record<'issued' | 'partial' | 'redeemed' | 'expired', { l: string; tone: BadgeTone }> = {
  // ⚠️ LES MOTS VIENNENT DU CŒUR (`VOUCHER_STATE_LABELS`), le TON reste à l'écran : le vocabulaire
  //    d'un état métier est le même partout, sa couleur de pastille appartient à la charte web.
  issued: { l: VOUCHER_STATE_LABELS.issued, tone: 'ok' },
  partial: { l: VOUCHER_STATE_LABELS.partial, tone: 'warn' },
  redeemed: { l: VOUCHER_STATE_LABELS.redeemed, tone: 'neutral' },
  expired: { l: VOUCHER_STATE_LABELS.expired, tone: 'danger' },
};

/**
 * LES CINQ TYPES DE BON — libellé, NATURE fiscale, icône et couleurs.
 *
 * ⚠️ IL EN EXISTAIT DEUX VERSIONS (relevé 2026-09-06) : une dans le registre « Titres & bons », une
 * autre dans la fiche client, avec des couleurs ET des dessins différents pour le même type. Forme A
 * du catalogue — deux écrans qui montrent le même bon ne peuvent pas le peindre autrement.
 *
 * ⚠️ LA COULEUR SUIT LA NATURE, et c'est ce qui rend la lecture immédiate : un TITRE a été PAYÉ
 * (violet, bleu, teal), une REMISE a été OFFERTE (AMBRE). C'est la distinction que `natureOfType`
 * (core-vouchers) impose déjà au fiscal — l'écran dit la même chose que le cœur. Même règle en caisse.
 *
 * ⚠️ L'ICÔNE VIENT DU JEU COMMUN (`IconName`), plus d'un chemin SVG recopié : les deux écrans du
 * back-office et la caisse montrent le MÊME dessin pour un même « acompte ». Un chemin recopié se
 * corrige à un endroit et pas à l'autre — c'est déjà arrivé sur le coupon.
 */
export const TYPE_TITRE: Record<string, { l: Translatable; nat: 'titre' | 'remise'; icon: IconName; bg: string; fg: string }> = {
  // ── TITRES (payés) ──
  avoir: { l: key('Avoir'), nat: 'titre', icon: 'receipt', bg: 'var(--violet-bg)', fg: 'var(--violet-fg)' },
  cadeau: { l: key('Chèque cadeau'), nat: 'titre', icon: 'gift', bg: 'var(--blue-bg)', fg: 'var(--blue-fg)' },
  acompte: { l: key('Acompte'), nat: 'titre', icon: 'wallet', bg: 'var(--teal-100)', fg: 'var(--teal-800)' },
  // ── REMISES (offertes) — AMBRE ──
  fidelite: { l: key('Bon de fidélité'), nat: 'remise', icon: 'star', bg: 'var(--amber-bg)', fg: 'var(--amber-fg)' },
  coupon: { l: key('Coupon de remise'), nat: 'remise', icon: 'percent', bg: 'var(--amber-bg)', fg: 'var(--amber-fg)' },
};
/**
 * Un type inconnu reste LISIBLE en neutre plutôt que de porter le nom d'un autre.
 * ⚠️ `help` et non le dessin d'un chèque cadeau : lui en donner l'apparence lui donnerait celle d'un
 * titre PAYÉ, ce qu'on ne sait justement pas (§23.0).
 */
export const typeUi = (kind: string) => {
  const ui = TYPE_TITRE[kind];
  // ⚠️ Le libellé se résout AU RENDU, jamais au chargement : la langue du compte n’est pas encore
  //    arrivée quand ce module s’évalue.
  return ui ? { ...ui, l: t(ui.l) }
    : { l: kind, nat: 'titre' as const, icon: 'help' as IconName, bg: 'var(--ink-100)', fg: 'var(--ink-600)' };
};

/**
 * COMMENT LE COMMERÇANT VOIT UNE CAGNOTTE — ton de pastille, et le motif quand elle est négative.
 *
 * ⚠️ LES DEUX CÔTÉS NE VOIENT PAS LE MÊME NOMBRE, ET C'EST VOULU (règle utilisateur 2026-08-31,
 * audit fidélité F22). Le CLIENT ne doit jamais lire un solde négatif — le wallet et les canaux
 * marketing passent par `customerFacingBalance`, qui plafonne à 0. Le COMMERÇANT, lui, lit le vrai
 * chiffre : c'est sa seule information pour comprendre pourquoi le prochain achat ne créditera rien.
 * ⚠️ LA CAUSE VIENT DU SERVEUR, LA PHRASE DU CŒUR (`NEGATIVE_BALANCE_CAUSE_TEXT`), en peu de mots :
 * « bon d'achat émis, puis article rendu », « gains utilisés, puis article rendu »… La caisse lit la
 * même phrase — elle ne peut pas dire deux choses selon l'écran. Le français est la clé, traduite ici.
 * ⚠️ UNE CAUSE ABSENTE SUR UN SOLDE NÉGATIF se dit « inconnue », jamais une cause générale qui
 * affirmerait un retour d'article qui n'a peut-être pas eu lieu.
 * ⚠️ ZÉRO N'EST PAS NÉGATIF : un solde nul reste un tiret discret, pas une alerte.
 */
export function merchantBalanceView(cents: number, cause: NegativeBalanceCause | null | undefined): { tone: BadgeTone; reason: string | null } {
  if (cents >= 0) return { tone: 'ok', reason: null };
  return { tone: 'danger', reason: t(NEGATIVE_BALANCE_CAUSE_TEXT[cause ?? 'unknown']) };
}

/**
 * LE TOTAL DU DÉTAIL DE CAGNOTTE, et ce qu'il dit du solde — le même contrôle qu'en caisse.
 *
 * ⚠️ LE TOTAL EST CELUI DE TOUT LE GRAND LIVRE, calculé par le serveur : la liste peut n'en montrer que les
 *    mouvements les plus récents, le total, lui, se compare toujours au solde — un écart affiché est donc
 *    toujours un vrai écart, jamais l'effet d'une liste tronquée.
 */
export function ledgerTotalView(ledgerTotalCents: number, balanceCents: number): { totalCents: number; gapCents: number } {
  return { totalCents: ledgerTotalCents, gapCents: balanceCents - ledgerTotalCents };
}

/**
 * LE BADGE WALLET DE L'EN-TÊTE — allumé seulement quand un pass ACTIF existe, comme en caisse : un client qui
 * consent sans qu'aucun pass n'existe n'a pas de wallet actif.
 */
export function walletBadgeOn(wallet: { hasPass: boolean; passActive: boolean }): boolean {
  return wallet.hasPass && wallet.passActive;
}

/**
 * CE QUE LA NOTE RGPD DIT D'UN PASS PAS ENCORE ÉMIS — la promesse du prochain cycle seulement quand le magasin
 * peut émettre. Sans clé, ou programme suspendu, aucun cycle ne l'émettra (§23.0) ; tant que l'état n'est pas
 * lu (`null`), la note ne promet rien.
 */
export function pendingPassNote(walletReady: boolean | null): 'next_cycle' | 'not_activated' | 'unknown' {
  return walletReady === true ? 'next_cycle' : walletReady === false ? 'not_activated' : 'unknown';
}
